/**
 * The v3 report, frozen for sharing.
 *
 * A snapshot has to survive the report changing underneath it: what is sent to
 * Raketech on a given day must still read the same months later, whatever the
 * claim logic does in the meantime. So the rows are computed here and stored,
 * rather than the inputs being stored and recomputed by the viewer.
 *
 * Months are stored as tuples rather than objects. A snapshot carries some four
 * thousand of them and D1 caps a single value at 2 MB.
 */
import type { ClaimRow, ClaimSummary, ClaimSource } from '../types/raketechFinalReportV3.types';
import type { ClaimAttributionSummary } from '../types/raketechFinalReportV4.types';
import { actionsFor, invoicedMonthsByBrand, markTotals, positionFor } from './raketechClaimMarks';
import type { MarkTotals } from './raketechClaimMarks';
import { matchesToTheCent, theirCurrentFigure } from './raketechAccess';
import { ftdSpan, writeDownOf, type WriteDown } from './raketechFocus';
import type { ClaimRow as ClaimRowV4 } from '../types/raketechFinalReportV4.types';

/** month, invoiced, ftds, earned, juneRecon, juneReported, restated, synced, ac */
export type SharedMonth = [string, number, number, number, number, number, number, number, number];

export interface SharedV3Source {
  source: ClaimSource;
  amount: number;
  available: boolean;
  marginApplied: boolean;
}

export interface SharedV3Row {
  key: string;
  accountId: string;
  acIds: string[];
  username: string;
  partner: string;
  brands: string[];
  geos: string[];
  currency: string;
  invoiced: number;
  earned: number;
  difference: number;
  chosenSource: ClaimSource;
  /** What Raketech's monthly reports paid on: acquisitions, revenue share or
   *  both. Where the reported deal paid per acquisition and the account earned
   *  revenue share, the account is marked rather than written down. */
  dealBasis: ClaimRow['dealBasis'];
  /** Set where a position Adwin has taken in correspondence rules out one of
   *  its own sources for this account. */
  concession?: string;
  candidates: SharedV3Source[];
  position: string;
  actions: string[];
  comments: string[];
  /** What their monthly reports paid out on, where the June file records it. */
  reported: number;
  /** How much of `earned` rests on that reported figure rather than what they
   *  later credited. */
  reportedLift: number;
  /** What their own file credits below what it reported, whatever values the row. */
  reportedVsCredited: number;
  gapMonths: number;
  gapInvoiced: number;
  gapReportedElsewhere: number;
  gapAbsent: number;
  months: SharedMonth[];
  /** v4: facts the focused views turn on. */
  facts?: {
    /** Raketech's own Partner Account classification in its July 2026 report. */
    pa: 'yes' | 'no' | 'mixed' | 'unknown';
    /** Account creation date and status per the July 2026 report. */
    created?: string | null;
    status?: string;
    /** First and last month with acquisitions on Raketech's monthly reports. */
    firstFtd?: string;
    lastFtd?: string;
    ftds: number;
    /** July 2026 report below Raketech's own monthly report after Nov 2024. */
    tailWrittenDown: number;
    /** Raketech's July "Adwin earnings" column for the account. */
    julyStated?: number;
    /** Whether Adwin holds any figure of its own for the account. */
    ownData: boolean;
    /** Raketech's current figure equals Adwin's invoicing to the cent, period
     *  by period (see `matchesToTheCent`). */
    exactMatch: boolean;
    /** Raketech's figure for the account as it stands now, whole period. */
    theirFigure: number;
    monthsWithFtds: number;
    dealCodes: string[];
    /** From the no-login list at the time of sharing. */
    noLogin: boolean;
    shared: boolean;
    /** Adwin's invoicing and Raketech's figure split at the end of the
     *  reconciled period, November 2024. */
    invoicedRecon: number;
    invoicedPostRecon: number;
    earnedTail: number;
    /** The three reductions, measured as on the Write-downs tab. */
    writeDown: WriteDown;
  };
}

export interface SharedV3Payload {
  /** 3: invoicing from the comparison endpoint. 4: every invoice line placed
   *  once by `shared/raketech/invoiceAttribution`, with its accounting below. */
  version: 3 | 4;
  generatedAt: string;
  period: { from: string; to: string };
  options: { applyMargin: boolean };
  /** v4 only: the identity all = attributed + unattributed, and what rested on size. */
  attribution?: ClaimAttributionSummary;
  /** v4 only: set when the snapshot is a selection of accounts sent for one
   *  purpose — the accounts Raketech classifies as not Partner Accounts, or the
   *  accounts it reported and later reduced — rather than the whole position.
   *  The viewer shows the note in place of the general introduction. */
  focus?: {
    kind: 'pa_no_players' | 'write_downs';
    title: string;
    note: string;
    /** How the selection was made, in words, so the recipient sees the filter. */
    selection: string;
  };
  totals: {
    accounts: number;
    invoiced: number;
    earned: number;
    shortGross: number;
    shortAccounts: number;
    overGross: number;
    overAccounts: number;
    reportedWriteDown: number;
    reportedWriteDownCommented: number;
  };
  marks: MarkTotals;
  rows: SharedV3Row[];
}

const r2 = (n: number) => Math.round((n || 0) * 100) / 100;

export function buildSharePayload(
  rows: ClaimRow[],
  summary: ClaimSummary,
  options: {
    applyMargin: boolean; lastMonth: string; version?: 3 | 4; focus?: SharedV3Payload['focus'];
    flags?: Record<string, { noLogin: boolean; shared: boolean }>;
  },
): SharedV3Payload {
  const brandMonths = invoicedMonthsByBrand(rows);
  // A focused selection keeps the order the tab showed; the whole position is
  // ordered by shortfall.
  const ordered = options.focus ? rows : [...rows].sort((a, b) => a.underpaid - b.underpaid);
  const attribution = (summary as ClaimSummary & { attribution?: ClaimAttributionSummary }).attribution;

  // Totals are taken from the rows given, so a selection totals itself. For
  // the whole position they equal the summary's own figures.
  const short = rows.filter((r) => r.underpaid < -1);
  const over = rows.filter((r) => r.underpaid > 1);
  const writtenDown = rows.filter((r) => r.reportedVsCredited > 1 || r.reportedLift > 1);
  const commented = writtenDown.filter((r) => r.reference.juneComments.length > 0);
  const wd = (r: ClaimRow) => Math.max(r.reportedVsCredited > 1 ? r.reportedVsCredited : 0, r.reportedLift > 1 ? r.reportedLift : 0);

  const facts = (row: ClaimRow): SharedV3Row['facts'] => {
    // Version 4 rows carry the fields the focused views need; the cast is the
    // one place the two row types meet.
    const v4 = row as unknown as ClaimRowV4;
    const span = ftdSpan(row);
    const paCount = row.offers.filter((o) => o.pa).length;
    const wd = writeDownOf(v4);
    const flags = options.flags?.[row.accountId];
    return {
      pa: row.offers.length === 0 ? 'unknown' : paCount === row.offers.length ? 'yes' : paCount === 0 ? 'no' : 'mixed',
      created: row.offers[0]?.created ?? null,
      status: [...new Set(row.offers.map((o) => o.status).filter(Boolean))].join(' / ') || undefined,
      firstFtd: span.first,
      lastFtd: span.last,
      ftds: span.total,
      monthsWithFtds: span.months,
      tailWrittenDown: r2(v4.tailWrittenDown || 0),
      julyStated: v4.reference.julyStatedAdwinEarnings,
      ownData: wd.ownData,
      exactMatch: matchesToTheCent(row),
      theirFigure: r2(theirCurrentFigure(row)),
      dealCodes: row.offers.map((o) => o.offer),
      noLogin: flags?.noLogin ?? false,
      shared: flags?.shared ?? false,
      invoicedRecon: r2(row.invoicedRecon),
      invoicedPostRecon: r2(row.invoicedPostRecon),
      earnedTail: r2(row.earnedTail),
      writeDown: {
        reported: r2(wd.reported), june: r2(wd.june), july: r2(wd.july), tail: r2(wd.tail),
        reportedToJune: r2(wd.reportedToJune), juneToJuly: r2(wd.juneToJuly), writeDown: r2(wd.writeDown),
        current: r2(wd.current), nulled: wd.nulled, ownData: wd.ownData, pct: wd.pct,
      },
    };
  };

  return {
    version: options.version ?? 3,
    generatedAt: new Date().toISOString(),
    period: { from: summary.reconMonths[0] || '2023-03', to: options.lastMonth },
    options: { applyMargin: options.applyMargin },
    attribution: options.version === 4 ? attribution : undefined,
    focus: options.focus,
    totals: {
      accounts: rows.length,
      invoiced: r2(rows.reduce((s, r) => s + r.invoicedTotal, 0)),
      earned: r2(rows.reduce((s, r) => s + r.earnedTotal, 0)),
      shortGross: r2(-short.reduce((s, r) => s + r.underpaid, 0)),
      shortAccounts: short.length,
      overGross: r2(over.reduce((s, r) => s + r.underpaid, 0)),
      overAccounts: over.length,
      reportedWriteDown: r2(writtenDown.reduce((s, r) => s + wd(r), 0)),
      reportedWriteDownCommented: r2(commented.reduce((s, r) => s + wd(r), 0)),
    },
    marks: markTotals(rows, brandMonths),
    rows: ordered.map((row) => ({
      key: row.key,
      accountId: row.accountId,
      acIds: row.acIds,
      username: row.username,
      partner: row.partner,
      brands: row.brands,
      geos: row.geos,
      currency: row.currency,
      invoiced: r2(row.invoicedTotal),
      earned: r2(row.earnedTotal),
      difference: r2(-row.underpaid),
      chosenSource: row.chosenSource,
      dealBasis: row.dealBasis,
      concession: row.concession,
      candidates: row.candidates
        .filter((c) => c.available)
        .map((c) => ({
          source: c.source, amount: r2(c.amount), available: true, marginApplied: c.marginApplied,
        })),
      position: positionFor(row),
      actions: actionsFor(row, brandMonths),
      comments: row.reference.juneComments,
      reported: r2(row.reference.raketechReported),
      reportedLift: r2(row.reportedLift),
      reportedVsCredited: r2(row.reportedVsCredited),
      gapMonths: row.coverage.gapMonths,
      gapInvoiced: r2(row.coverage.gapInvoiced),
      gapReportedElsewhere: r2(row.coverage.gapReportedElsewhere),
      gapAbsent: r2(row.coverage.gapAbsent),
      facts: options.version === 4 ? facts(row) : undefined,
      months: row.monthly
        .filter((m) => m.invoiced || m.earned || m.juneRecon || m.restated || m.synced || m.ac)
        .map((m): SharedMonth => [
          m.month, r2(m.invoiced), m.ftds, r2(m.earned), r2(m.juneRecon),
          r2(m.juneReported), r2(m.restated), r2(m.synced), r2(m.ac),
        ]),
    })),
  };
}

export const isSharedV3 = (data: unknown): data is SharedV3Payload => {
  const v = !!data && typeof data === 'object' ? (data as { version?: number }).version : undefined;
  return v === 3 || v === 4;
};
